/**
 * Kalendar widgeti (2026-10-18): barcha eslatma va voqealar bir joyda -- eslatmalar (Tasks), ishga chiqish / orientatsiya
 * kuni, carrier to'lov muddati, hujjat muddatlari, driver "bo'sh bo'ladi" sanasi, biriktirish tugashi. Doira backend'da.
 * S -- bugun (katta kun raqami) + 2 ta yaqin voqea; M -- bugun bloki + 3 ta yaqin voqea; L -- oy to'ri (nuqtalar tur
 * bo'yicha) + tanlangan kun ro'yxati. Yuqori panelda -- bugungi ochiq voqealar soni. Bosilsa driver / to'lovlar ochiladi.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { cn, dayjs } from "@/lib/utils";
import { useNow } from "@/lib/useNow";
import type { Me } from "@/features/auth/types";
import type { WidgetItem } from "../types";
import { useCalendarEvents } from "./api";
import type { CalendarConfig, CalendarKind } from "./config";
import { dotKinds, gridRange, groupByDate, monthGrid, relativeDay, shiftMonth, upcoming, type CalendarEvent } from "./dates";

type Props = { item: WidgetItem<CalendarConfig>; me: Me };

/** Tur rangi -- faqat tokenlar: eslatma ko'k, ishga chiqish yashil, to'lov to'q sariq, muddat qizil, qolgani kulrang */
export const DOT: Record<CalendarKind, string> = {
  reminder: "bg-primary",
  start: "bg-success",
  fee: "bg-warning",
  expiry: "bg-danger",
  available: "bg-ink-soft",
  assignment: "bg-muted",
};

const tzOf = (me: Me) => me.timezone || "Asia/Tashkent";

function useToday(me: Me): string {
  const now = useNow("minute");
  return dayjs(now).tz(tzOf(me)).format("YYYY-MM-DD");
}

function useLang(): string {
  const { i18n } = useTranslation();
  return i18n.resolvedLanguage ?? i18n.language;
}

const utcOf = (key: string) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const fmt = (lang: string, opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(lang, { ...opts, timeZone: "UTC" });
const monthTitle = (lang: string, year: number, month: number) => fmt(lang, { month: "long", year: "numeric" }).format(new Date(Date.UTC(year, month - 1, 1)));
const weekdayShort = (lang: string, mondayIndex: number) => fmt(lang, { weekday: "short" }).format(new Date(Date.UTC(2024, 0, 1 + mondayIndex))); // 2024-01-01 -- dushanba
const weekdayLong = (lang: string, key: string) => fmt(lang, { weekday: "long" }).format(utcOf(key));
const dayShort = (lang: string, key: string) => fmt(lang, { day: "numeric", month: "short" }).format(utcOf(key));
const monthShort = (lang: string, key: string) => fmt(lang, { month: "short" }).format(utcOf(key));

/** Ko'rinadigan voqealar: oy to'ri oralig'i, bajarilganlar sozlamaga qarab */
function useEvents(c: CalendarConfig, year: number, month: number) {
  const range = gridRange(year, month);
  const q = useCalendarEvents(range.from, range.to, c.kinds);
  const events = useMemo(() => (q.data?.events ?? []).filter((e) => c.done || !e.done), [q.data, c.done]);
  return { events, loading: q.isPending, failed: q.isError };
}

function DayLabel({ day, today, lang }: { day: string; today: string; lang: string }) {
  const { t } = useTranslation("widgets");
  const rel = relativeDay(day, today);
  return <>{rel === "other" ? dayShort(lang, day) : t(`calendar.${rel}`)}</>;
}

/** Katta o'lchamdagi kun ro'yxati qatori: nuqta, vaqt, ism va tur */
function EventRow({ e }: { e: CalendarEvent }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => e.href && navigate(e.href)}
      className={cn("flex w-full items-center gap-2 rounded-[8px] px-1.5 py-[3px] text-left transition-colors", e.href ? "hover:bg-fill" : "cursor-default")}
    >
      <span className={cn("h-2 w-2 shrink-0 rounded-full", DOT[e.kind], e.done && "opacity-40")} aria-hidden />
      <span className="w-[38px] shrink-0 text-[11px] tabular-nums text-muted">{e.time ?? ""}</span>
      <span className="min-w-0 flex-1 leading-tight">
        <span className={cn("block truncate text-[12px] font-medium", e.done && "text-muted line-through")}>{e.title}</span>
        {e.subtitle && <span className="block truncate text-[11px] text-muted">{e.subtitle}</span>}
      </span>
    </button>
  );
}

/** Kichik va o'rta o'lcham uchun qator: rangli chiziq, ism, ikkinchi qatorda "Bugun 14:00 · tur" (Apple kalendar widgeti kabi) */
function EventBar({ e, today, lang }: { e: CalendarEvent; today: string; lang: string }) {
  const navigate = useNavigate();
  const { t } = useTranslation("widgets");
  const rel = relativeDay(e.date, today);
  // Bugungi voqeada "Bugun" yozilmaydi -- katta sana allaqachon bugunni ko'rsatadi; ertaga va boshqa kunlar nomi bilan
  const when = [rel === "today" ? "" : rel === "tomorrow" ? t("calendar.tomorrow") : dayShort(lang, e.date), e.time].filter(Boolean).join(" ");
  const line = [when, e.subtitle].filter(Boolean).join(" · ");
  return (
    <button
      type="button"
      onClick={() => e.href && navigate(e.href)}
      className={cn("flex w-full items-stretch gap-1.5 rounded-[6px] text-left transition-colors", e.href ? "hover:bg-fill" : "cursor-default")}
    >
      <span className={cn("w-[3px] shrink-0 rounded-full", DOT[e.kind])} aria-hidden />
      <span className="min-w-0 flex-1 py-px leading-tight">
        <span className="block truncate text-[11px] font-semibold">{e.title}</span>
        <span className="block truncate text-[10px] text-muted">{line}</span>
      </span>
    </button>
  );
}

function TodayBlock({ today, lang }: { today: string; lang: string }) {
  return (
    <div className="shrink-0">
      <div className="text-[10px] font-semibold tracking-[0.08em] text-ink-soft uppercase">{weekdayLong(lang, today)}</div>
      <div className={cn("font-semibold leading-none tracking-[-0.03em] tabular-nums", "text-[36px]")}>{Number(today.slice(-2))}</div>
      <div className="mt-0.5 text-[11px] text-muted capitalize">{monthShort(lang, today)}</div>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="flex h-full items-center justify-center text-[12px] text-muted">{text}</div>;
}

export function CalendarView({ item, me }: Props) {
  const { t } = useTranslation("widgets");
  const lang = useLang();
  const today = useToday(me);
  const [cursor, setCursor] = useState<{ year: number; month: number } | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const todayYear = Number(today.slice(0, 4));
  const todayMonth = Number(today.slice(5, 7));
  const { year, month } = cursor ?? { year: todayYear, month: todayMonth };
  const { events, loading, failed } = useEvents(item.config, year, month);
  const byDate = useMemo(() => groupByDate(events), [events]);

  if (item.size === "s") {
    const all = upcoming(events, today, 99);
    const next = all.slice(0, 2);
    return (
      <div className="flex h-full flex-col px-3 pt-2.5 pb-2">
        <div className="truncate text-[10px] font-semibold tracking-[0.08em] text-ink-soft uppercase">{weekdayLong(lang, today)}</div>
        <div className="text-[36px] leading-none font-semibold tracking-[-0.03em] tabular-nums">{Number(today.slice(-2))}</div>
        <div className="mt-auto space-y-1">
          {next.length ? next.map((e) => <EventBar key={e.id} e={e} today={today} lang={lang} />) : !loading && <div className="text-[11px] text-muted">{t(failed ? "failed" : "calendar.empty")}</div>}
          {all.length > 2 && <div className="pl-[9px] text-[10px] text-muted tabular-nums">+{all.length - 2}</div>}
        </div>
      </div>
    );
  }

  if (item.size === "m") {
    const next = upcoming(events, today, 3);
    return (
      <div className="flex h-full gap-3 px-3 py-2.5">
        <TodayBlock today={today} lang={lang} />
        <div className="min-w-0 flex-1 border-l border-line pl-3">
          {next.length ? (
            <div className="space-y-1.5">{next.map((e) => <EventBar key={e.id} e={e} today={today} lang={lang} />)}</div>
          ) : (
            !loading && <Empty text={t(failed ? "failed" : "calendar.empty")} />
          )}
        </div>
      </div>
    );
  }

  const cells = monthGrid(year, month);
  const day = selected ?? today;
  const dayEvents = byDate.get(day) ?? [];
  const go = (delta: number) => setCursor(shiftMonth(year, month, delta));
  const isCurrent = year === todayYear && month === todayMonth;
  const btn = "flex h-6 w-6 items-center justify-center rounded-full text-ink-soft transition-colors hover:bg-fill hover:text-ink";
  return (
    <div className="flex h-full flex-col px-3 pt-2 pb-1.5">
      <div className="flex items-center justify-between">
        <button type="button" className={btn} onClick={() => go(-1)} aria-label={t("calendar.prev")}>
          <ChevronLeft size={16} strokeWidth={1.75} />
        </button>
        <button
          type="button"
          className={cn("rounded-full px-2 text-[13px] font-semibold capitalize", !isCurrent && "hover:bg-fill")}
          onClick={() => {
            setCursor(null);
            setSelected(null);
          }}
          title={t("calendar.today")}
        >
          {monthTitle(lang, year, month)}
        </button>
        <button type="button" className={btn} onClick={() => go(1)} aria-label={t("calendar.next")}>
          <ChevronRight size={16} strokeWidth={1.75} />
        </button>
      </div>
      <div className="mt-1 grid grid-cols-7 text-center text-[10px] font-medium tracking-wide text-muted uppercase">
        {Array.from({ length: 7 }, (_, i) => (
          <span key={i}>{weekdayShort(lang, i)}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-px">
        {cells.map((cell) => {
          const dots = dotKinds(byDate.get(cell.key));
          const isToday = cell.key === today;
          const isSelected = cell.key === day;
          return (
            <button
              key={cell.key}
              type="button"
              onClick={() => setSelected(cell.key)}
              className={cn("flex h-[25px] flex-col items-center rounded-[7px] text-[11px] tabular-nums transition-colors hover:bg-fill", !cell.inMonth && "text-faint", isSelected && "bg-fill")}
              aria-label={dayShort(lang, cell.key)}
              aria-pressed={isSelected}
            >
              <span className={cn("flex h-[16px] min-w-[16px] items-center justify-center rounded-full px-1 leading-none", isToday && "bg-ink font-semibold text-surface")}>{cell.day}</span>
              <span className="mt-[2px] flex h-[4px] gap-[2px]">
                {dots.map((k) => (
                  <span key={k} className={cn("h-[4px] w-[4px] rounded-full", DOT[k])} />
                ))}
              </span>
            </button>
          );
        })}
      </div>
      <div className="mt-1 flex min-h-0 flex-1 flex-col border-t border-line pt-1">
        <div className="flex items-baseline justify-between px-1.5 text-[11px] text-muted">
          <span className="capitalize">
            <DayLabel day={day} today={today} lang={lang} />
            {relativeDay(day, today) !== "other" && <span className="text-faint"> · {dayShort(lang, day)}</span>}
          </span>
          {dayEvents.length > 0 && <span className="tabular-nums">{dayEvents.length}</span>}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {dayEvents.length ? dayEvents.map((e) => <EventRow key={e.id} e={e} />) : !loading && <Empty text={t(failed ? "failed" : "calendar.emptyDay")} />}
        </div>
      </div>
    </div>
  );
}

export function CalendarGlance({ item, me }: Props) {
  const { t } = useTranslation("widgets");
  const today = useToday(me);
  const { events } = useEvents(item.config, Number(today.slice(0, 4)), Number(today.slice(5, 7)));
  const count = events.filter((e) => e.date === today && !e.done).length;
  return (
    <span className="inline-flex items-center gap-1.5" title={t("calendar.title")}>
      <CalendarDays size={13} strokeWidth={1.75} className="text-muted" aria-hidden />
      <span className={cn("text-[12px] font-semibold tabular-nums", count === 0 && "text-muted")}>{t("calendar.glance", { count })}</span>
    </span>
  );
}
